import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../../common/types';
import type { ReelsPlanRefusal } from '../../../../../../redux/api/fymAPI.types';
import { useSMLRouter } from '../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../routing/sml/SMLRoutes';
import { useOpenSongPage } from '../../../../../smartLinks/catalog/song/page/useOpenSongPage';
import { SMLButton } from '../../../../components/primitives/SMLButton';
import { SMLUpgradeFeature } from '../../../../config/music24PlanCapabilities';
import { useUpgradePrompt } from '../../../../hooks/useUpgradePrompt';
import { reelsPlanMessages } from './reelsPlanMessages';
import { reelsPlanRefusalMessage } from './reelsPlanModel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the AI reels plan; each part uses its own slice of it */
import styles from './ReelsPlan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelsPlanRefusalLineProps {
  /** A refusal that takes the place of the summary and of "Make the plan" (frame f2). */
  refusal: ReelsPlanRefusal;
  catalogItemUuid: string;
  songTitle: string;
}

/**
 * The foot of the setup when the plan cannot start for a reason that is not about its size or its
 * cost (design rollout-plan f2): the sentence, and the one step that fixes it. A workspace whose
 * plan does not cover the AI reels plan of this song, or the posts a day, gets the plan offer, a song with no file opens
 * its song page at the files, an artist with no channel for a reel opens Channels.
 */
const ReelsPlanRefusalLineComponent: React.FC<ReelsPlanRefusalLineProps> = ({
  refusal,
  catalogItemUuid,
  songTitle,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const openUpgrade = useUpgradePrompt();
  const openSong = useOpenSongPage();

  let action: React.ReactNode = null;
  if (refusal === 'postsPerDayNeedsPlan') {
    action = (
      <SMLButton onClick={() => openUpgrade(SMLUpgradeFeature.generic)}>
        {intl.formatMessage(reelsPlanMessages.seePlans)}
      </SMLButton>
    );
  } else if (refusal === 'needsPlan') {
    action = (
      <SMLButton onClick={() => openUpgrade(SMLUpgradeFeature.oldSongs, songTitle)}>
        {intl.formatMessage(reelsPlanMessages.seePlans)}
      </SMLButton>
    );
  } else if (refusal === 'noMaster') {
    action = (
      <SMLButton onClick={() => openSong(catalogItemUuid, 'files')}>
        {intl.formatMessage(reelsPlanMessages.openSong)}
      </SMLButton>
    );
  } else if (refusal === 'noChannels') {
    action = (
      <SMLButton onClick={() => router.push(SMLRoutes.promoteChannels)}>
        {intl.formatMessage(reelsPlanMessages.openChannels)}
      </SMLButton>
    );
  }

  return (
    <div className={styles.foot}>
      <span className={styles.sum}>{intl.formatMessage(reelsPlanRefusalMessage(refusal))}</span>
      <span className={styles.grow} />
      {action}
    </div>
  );
};

export const ReelsPlanRefusalLine = TypedMemo(ReelsPlanRefusalLineComponent);
